"use client";

import { useEffect, useMemo, useState } from "react";
import { RiDeleteBin6Line } from "react-icons/ri";
import { MdDragIndicator } from "react-icons/md";
import { IoSearchOutline } from "react-icons/io5";
import { MdKeyboardArrowDown } from "react-icons/md";
import ConfirmPopUp from "./confirmPopUp";
import recommendGameAPI from "../../services/recommendGame/recommendGameAPI";
import mainAPI from "../../services/main/mainAPI";
import categoryAndGroupAPI from "../../services/categoryAndGroup/categoryAndGroupAPI";

type GameItem = {
    id: number;
    uuid?: string;
    exercise_name: string;
    game_type: string | null;
    thumbnail: string | null;
    subject?: string | null;
    group_name?: string | null;
    create_by?: string;
    game_default?: boolean;
    source?: string;
};

type FilterOption = {
    id: number | string;
    name: string;
};

type RecommendGamePopupProps = {
    onClose: () => void;
};

const MAX_RECOMMEND = Number.POSITIVE_INFINITY;

function toGameArray(res: unknown): GameItem[] {
    if (Array.isArray(res)) return res as GameItem[];
    if (res && Array.isArray((res as { data?: unknown }).data)) {
        return (res as { data: GameItem[] }).data;
    }
    return [];
}

function toFilterOptionArray(res: unknown): FilterOption[] {
    if (Array.isArray(res)) {
        return (res as Array<Record<string, unknown>>)
            .map((item) => ({
                id: String(item.id ?? ""),
                name: String(item.name ?? ""),
            }))
            .filter((item) => item.name.length > 0);
    }
    if (res && Array.isArray((res as { data?: unknown }).data)) {
        return ((res as { data: Array<Record<string, unknown>> }).data)
            .map((item) => ({
                id: String(item.id ?? ""),
                name: String(item.name ?? ""),
            }))
            .filter((item) => item.name.length > 0);
    }
    return [];
}

function normalizeTeacherGames(res: unknown): GameItem[] {
    return toGameArray(res).filter((game) => {
        const source = String(game.source ?? "").toLowerCase();
        return source !== "sequence" && !Boolean(game.game_default);
    });
}

function normalizeDefaultGames(res: unknown): GameItem[] {
    return toGameArray(res).filter((game) => {
        const source = String(game.source ?? "").toLowerCase();
        return source === "game" && Boolean(game.game_default);
    });
}

function getThumbnailUrl(path: string | null | undefined): string | null {
    if (!path) return null;
    if (path.startsWith("http")) return path;
    return `https://thaipilacreate.eef.or.th/api${path}`;
}

function gameTypeLabel(gameType: string | null | undefined): string {
    if (!gameType) return "-";
    const map: Record<string, string> = {
        situation: "Situation",
        find_the_match: "Find the Match",
        game_show_quiz: "Game Show Quiz",
        whack_a_mole: "Whack a Mole",
        "air_plan/flying_fruits": "Flying Fruits",
        flip_cards: "Flip Cards",
        flappy_bird: "Flappy Chicken",
        complete_the_sentence: "Complete the Sentence",
        anagram: "Anagram",
    };
    return map[gameType] ?? gameType;
}

export default function RecommendGamePopup({ onClose }: RecommendGamePopupProps) {
    const [originalRecommendIds, setOriginalRecommendIds] = useState<Set<number>>(new Set());
    const [leftGames, setLeftGames] = useState<GameItem[]>([]);
    const [teacherGames, setTeacherGames] = useState<GameItem[]>([]);
    const [defaultGames, setDefaultGames] = useState<GameItem[]>([]);
    const [showDefaultGames, setShowDefaultGames] = useState(false);
    const [isLoading, setIsLoading] = useState(true);

    const [searchText, setSearchText] = useState("");
    const [filterSubject, setFilterSubject] = useState("");
    const [filterGroup, setFilterGroup] = useState("");
    const [filterGameType, setFilterGameType] = useState("");
    const [categories, setCategories] = useState<FilterOption[]>([]);
    const [groups, setGroups] = useState<FilterOption[]>([]);

    const [dragItem, setDragItem] = useState<GameItem | null>(null);
    const [dragSource, setDragSource] = useState<"left" | "right" | null>(null);
    const [dragOverLeftIndex, setDragOverLeftIndex] = useState<number | null>(null);
    const [isDropZoneOver, setIsDropZoneOver] = useState(false);

    const [confirmSave, setConfirmSave] = useState(false);
    const [confirmDeleteTarget, setConfirmDeleteTarget] = useState<GameItem | null>(null);
    const [isSaving, setIsSaving] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);

    useEffect(() => {
        const fetchData = async () => {
            setIsLoading(true);
            try {
                const [recRes, teacherRes, defaultRes, categoriesRes, groupsRes] = await Promise.all([
                    recommendGameAPI.getAllRecommendGames(),
                    mainAPI.getAllLessonLibraryExceptDefaultGames(),
                    mainAPI.getAllLessonLibraryGameDefault(),
                    categoryAndGroupAPI.getAllCategories(),
                    categoryAndGroupAPI.getAllGroups(),
                ]);

                const recGames = toGameArray(recRes);
                const teacherGamesData = normalizeTeacherGames(teacherRes);
                const defaultGamesData = normalizeDefaultGames(defaultRes);
                const categoriesData = toFilterOptionArray(categoriesRes);
                const groupsData = toFilterOptionArray(groupsRes);

                setOriginalRecommendIds(new Set(recGames.map((g) => g.id)));
                setLeftGames(recGames);
                setTeacherGames(teacherGamesData);
                setDefaultGames(defaultGamesData);
                setCategories(categoriesData);
                setGroups(groupsData);
            } catch {
                console.log("Failed to fetch games");
            } finally {
                setIsLoading(false);
            }
        };
        fetchData();
    }, []);

    const allGames = useMemo(
        () => (showDefaultGames ? defaultGames : teacherGames),
        [showDefaultGames, defaultGames, teacherGames],
    );

    const handleToggleDefaultGames = () => {
        setShowDefaultGames((prev) => !prev);
        setSearchText("");
        setFilterSubject("");
        setFilterGroup("");
        setFilterGameType("");
    };

    const leftGameIds = new Set(leftGames.map((g) => g.id));
    const isAtMax = leftGames.length >= MAX_RECOMMEND;
    const newlyAddedIds = leftGames
        .filter((g) => !originalRecommendIds.has(g.id))
        .map((g) => g.id);

    const uniqueGameTypes = Array.from(
        new Set(allGames.map((g) => g.game_type).filter((type): type is string => Boolean(type)))
    );

    const filteredRightGames = allGames.filter((game) => {
        const normalizedSearch = searchText.trim().toLowerCase();
        const exerciseName = String(game.exercise_name ?? "").toLowerCase();
        const gameSubject = String(game.subject ?? "");
        const gameGroup = String(game.group_name ?? "");
        const gameType = String(game.game_type ?? "");

        if (normalizedSearch && !exerciseName.includes(normalizedSearch)) {
            return false;
        }
        if (filterSubject && gameSubject !== filterSubject) {
            return false;
        }
        if (filterGroup && gameGroup !== filterGroup) {
            return false;
        }
        if (filterGameType && gameType !== filterGameType) {
            return false;
        }
        return true;
    });

    const handleSelectGame = (game: GameItem) => {
        if (leftGameIds.has(game.id)) return;
        setLeftGames((prev) => [...prev, game]);
    };

    const syncRecommendOrder = async (orderedGames: GameItem[]) => {
        if (!orderedGames || orderedGames.length === 0) return;
        try {
            await Promise.all(
                orderedGames.map((g, idx) =>
                    recommendGameAPI.updateNoRecommendGame({ gameId: g.id, no: idx + 1 }),
                ),
            );
        } catch (err) {
            console.log("Failed to sync recommend order:", err);
        }
    };

    const handleDragStartRight = (e: React.DragEvent, game: GameItem) => {
        setDragItem(game);
        setDragSource("right");
        e.dataTransfer.effectAllowed = "copy";
    };

    const handleDragStartLeft = (e: React.DragEvent, game: GameItem) => {
        setDragItem(game);
        setDragSource("left");
        e.dataTransfer.effectAllowed = "move";
    };

    const handleDropOnLeftPanel = (e: React.DragEvent) => {
        e.preventDefault();
        setIsDropZoneOver(false);
        if (!dragItem) return;

        if (dragSource === "right") {
            if (!leftGameIds.has(dragItem.id)) {
                const updated = [...leftGames, dragItem];
                setLeftGames(updated);
                // sync only items that already exist on server
                const toSync = updated.filter((g) => originalRecommendIds.has(g.id));
                void syncRecommendOrder(toSync);
            }
        }
        setDragItem(null);
        setDragSource(null);
        setDragOverLeftIndex(null);
    };

    const handleDropOnLeftItem = (e: React.DragEvent, targetIndex: number) => {
        e.preventDefault();
        e.stopPropagation();
        setDragOverLeftIndex(null);
        if (!dragItem) return;

        if (dragSource === "left") {
            const fromIndex = leftGames.findIndex((g) => g.id === dragItem.id);
            if (fromIndex === -1 || fromIndex === targetIndex) return;
            const updated = [...leftGames];
            updated.splice(fromIndex, 1);
            updated.splice(targetIndex, 0, dragItem);
            setLeftGames(updated);
            const toSync = updated.filter((g) => originalRecommendIds.has(g.id));
            void syncRecommendOrder(toSync);
        } else if (dragSource === "right") {
            if (leftGameIds.has(dragItem.id)) return;
            const updated = [...leftGames];
            updated.splice(targetIndex, 0, dragItem);
            setLeftGames(updated);
            const toSync = updated.filter((g) => originalRecommendIds.has(g.id));
            void syncRecommendOrder(toSync);
        }

        setDragItem(null);
        setDragSource(null);
    };

    const handleDeleteLeft = (game: GameItem) => {
        setConfirmDeleteTarget(game);
    };

    const confirmDelete = async () => {
        if (!confirmDeleteTarget) return;
        const isOriginal = originalRecommendIds.has(confirmDeleteTarget.id);
        if (isOriginal) {
            setIsDeleting(true);
            try {
                await recommendGameAPI.deleteRecommendGame(confirmDeleteTarget.id);
                setOriginalRecommendIds((prev) => {
                    const next = new Set(prev);
                    next.delete(confirmDeleteTarget.id);
                    return next;
                });
            } catch {
                console.log("Failed to delete recommend game");
            } finally {
                setIsDeleting(false);
            }
        }
        const nextLeft = leftGames.filter((g) => g.id !== confirmDeleteTarget.id);
        setLeftGames(nextLeft);
        const nextOriginalSet = new Set(originalRecommendIds);
        if (isOriginal) nextOriginalSet.delete(confirmDeleteTarget.id);
        const toSync = nextLeft.filter((g) => nextOriginalSet.has(g.id));
        void syncRecommendOrder(toSync);
        setConfirmDeleteTarget(null);
    };

    const handleSave = () => {
        if (newlyAddedIds.length === 0) {
            onClose();
            return;
        }
        setConfirmSave(true);
    };

    const confirmSaveAction = async () => {
        setIsSaving(true);
        try {
            await recommendGameAPI.createRecommendGame({ gameId: newlyAddedIds });
            setOriginalRecommendIds((prev) => {
                const next = new Set(prev);
                for (const id of newlyAddedIds) next.add(id);
                return next;
            });

            const toSync = leftGames.filter((g) => newlyAddedIds.includes(g.id) || originalRecommendIds.has(g.id));
            await syncRecommendOrder(toSync);

            setConfirmSave(false);
            onClose();
        } catch {
            console.log("Failed to save recommend games");
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
            <div className="flex h-[88vh] w-full max-w-240 flex-col overflow-hidden rounded-xl border border-[#dadada] bg-[#ffff] shadow-[0_16px_40px_rgba(0,0,0,0.28)]">
                <div className="flex items-start justify-between px-6 pb-1 pt-4">
                    <div>
                        <h2 className="text-lg font-semibold leading-none text-[#2b2b2b]">เพิ่มเกมแนะนำ</h2>
                        <p className="mt-2 text-sm text-[#808080]">
                            เลือกเกมที่ต้องการแสดงในหมวดเกมแนะนำ โดยสามารถเลือกจำนวนการแสดงผลได้
                        </p>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        className="ml-4 mt-1 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#d9d9d9] text-sm text-[#7f7f7f] transition hover:bg-[#cfcfcf] hover:text-[#666] cursor-pointer"
                    >
                        ✕
                    </button>
                </div>

                {isLoading ? (
                    <div className="flex flex-1 items-center justify-center">
                        <div className="h-8 w-8 animate-spin rounded-full border-4 border-sky-500 border-t-transparent" />
                    </div>
                ) : (
                    <div className="flex min-h-0 flex-1 flex-col px-6 pb-4">
                        <div className="mb-3 flex items-center justify-between rounded-lg border border-[#e8e8e8] bg-[#fafafa] px-4 py-3">
                            <div>
                                <p className="text-sm font-semibold text-[#2b2b2b]">แสดงเกม Default</p>
                                <p className="mt-0.5 text-xs text-[#808080]">
                                    {showDefaultGames
                                        ? "กำลังแสดงเกม Default เท่านั้น"
                                        : "กำลังแสดงเกมของครู (ไม่รวม Default)"}
                                </p>
                            </div>
                            <button
                                type="button"
                                role="switch"
                                aria-checked={showDefaultGames}
                                onClick={handleToggleDefaultGames}
                                className={`relative inline-flex h-7 w-12 shrink-0 cursor-pointer items-center rounded-full transition ${showDefaultGames ? "bg-[#1497dc]" : "bg-[#cfcfcf]"}`}
                            >
                                <span
                                    className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition ${showDefaultGames ? "translate-x-6" : "translate-x-1"}`}
                                />
                            </button>
                        </div>

                        <div className="grid grid-cols-4 gap-3 pb-3 pt-1">
                            <div className="relative">
                                <input
                                    type="text"
                                    placeholder="ค้นหาชื่อเกม"
                                    value={searchText}
                                    onChange={(e) => setSearchText(e.target.value)}
                                    className="h-11 w-full rounded-lg border border-[#d7d7d7] bg-white py-2 pl-4 pr-9 text-md text-[#2B2B2B] outline-none placeholder:text-[#c4c4c4] focus:border-[#b8b8b8]"
                                />
                                <IoSearchOutline className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-md text-[#d0d0d0]" />
                            </div>

                            <div className="relative">
                                <select
                                    value={filterSubject}
                                    onChange={(e) => setFilterSubject(e.target.value)}
                                    className="h-11 w-full appearance-none rounded-lg border border-[#d7d7d7] bg-white px-4 text-md text-[#2B2B2B] outline-none cursor-pointer" 
                                >
                                    <option value="">วิชา</option>
                                    {categories.map((category) => (
                                        <option key={category.id} value={category.name}>
                                            {category.name}
                                        </option>
                                    ))}
                                </select>
                                <MdKeyboardArrowDown className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-[#2B2B2B]" />
                            </div>

                            <div className="relative">
                                <select
                                    value={filterGroup}
                                    onChange={(e) => setFilterGroup(e.target.value)}
                                    className="h-11 w-full appearance-none rounded-lg border border-[#d7d7d7] bg-white px-4 text-md text-[#2B2B2B] outline-none cursor-pointer"
                                >
                                    <option value="">กลุ่ม</option>
                                    {groups.map((group) => (
                                        <option key={group.id} value={group.name}>
                                            {group.name}
                                        </option>
                                    ))}
                                </select>
                                <MdKeyboardArrowDown className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-[#2B2B2B]" />
                            </div>

                            <div className="relative">
                                <select
                                    value={filterGameType}
                                    onChange={(e) => setFilterGameType(e.target.value)}
                                    className="h-11 w-full appearance-none rounded-lg border border-[#d7d7d7] bg-white px-4 text-md text-[#2B2B2B] outline-none cursor-pointer"
                                >
                                    <option value="">ประเภทเกม</option>
                                    {uniqueGameTypes.map((type) => (
                                        <option key={type} value={type}>
                                            {gameTypeLabel(type)}
                                        </option>
                                    ))}
                                </select>
                                <MdKeyboardArrowDown className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-[#2B2B2B]" />
                            </div>
                        </div>

                        <div className="grid grid-cols-2 gap-3 pb-1">
                            <div className="flex items-center justify-between px-1">
                                <span className="text-lg font-semibold text-[#2b2b2b]">รายการที่เลือกแล้ว</span>
                                {/* <span className={`text-sm font-normal ${isAtMax ? "text-red-500" : "text-[#9a9a9a]"}`}>
                                    เลือกได้สูงสุด 4 เกม
                                </span> */}
                            </div>
                            <div className="px-1">
                                <span className="text-lg font-semibold text-[#2b2b2b]">
                                    {showDefaultGames ? "รายการเกม Default" : "รายการเกมทั้งหมด"}
                                </span>
                            </div>
                        </div>

                        <div className="grid min-h-0 flex-1 grid-cols-2 gap-3">
                            <div
                                className={`min-h-0 max-h-[60vh] overflow-y-auto rounded-lg p-0.5 transition ${isDropZoneOver ? "bg-sky-50" : ""}`}
                                onDragOver={(e) => {
                                    e.preventDefault();
                                    setIsDropZoneOver(true);
                                }}
                                onDragLeave={() => setIsDropZoneOver(false)}
                                onDrop={handleDropOnLeftPanel}
                            >
                                {leftGames.length === 0 ? (
                                    <div
                                        className={`flex h-full min-h-32 items-center justify-center rounded-lg border-2 border-dashed text-[18px] text-[#bbb] ${isDropZoneOver ? "border-sky-400 bg-sky-50" : "border-[#ddd]"}`}
                                    >
                                        ลากเกมมาวางที่นี่
                                    </div>
                                    ) : (
                                    <div className="space-y-2.5">
                                        {leftGames.map((game, index) => {
                                            const thumbUrl = getThumbnailUrl(game.thumbnail);
                                            const isNew = !originalRecommendIds.has(game.id);
                                            const isOver = dragOverLeftIndex === index;
                                            return (
                                                <div
                                                    key={game.id}
                                                    draggable
                                                    onDragStart={(e) => handleDragStartLeft(e, game)}
                                                    onDragOver={(e) => {
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                        setDragOverLeftIndex(index);
                                                    }}
                                                    onDragLeave={() => setDragOverLeftIndex(null)}
                                                    onDrop={(e) => handleDropOnLeftItem(e, index)}
                                                    className={`flex min-h-24 cursor-grab items-center gap-2 rounded-lg border border-[#dedede] bg-white px-3 py-2 transition active:cursor-grabbing ${isNew ? "border-sky-200 bg-sky-50" : ""} ${isOver ? "border-sky-400 shadow-sm" : ""}`}
                                                >
                                                    <MdDragIndicator className="shrink-0 text-xl text-[#28a9ff]" />
                                                    <div className="h-13 w-20 shrink-0 overflow-hidden rounded-md border border-[#ececec] bg-[#e0e0e0]">
                                                        {thumbUrl ? (
                                                            <img
                                                                src={thumbUrl}
                                                                alt={game.exercise_name}
                                                                className="h-full w-full object-cover"
                                                            />
                                                        ) : null}
                                                    </div>
                                                    <div className="min-w-0 flex-1 leading-none">
                                                        <p className="truncate text-xs font-bold text-[#2b2b2b]">
                                                            {game.exercise_name}
                                                        </p>
                                                        {game.create_by && (
                                                            <p className="mt-1 truncate text-xs text-[#5f5f5f]">
                                                                โดย {game.create_by}
                                                            </p>
                                                        )}
                                                        <span className="mt-1.5 inline-block rounded-full bg-[#FDEF5C] px-2 py-1 text-xs font-semibold leading-none text-[#2B2B2B]">
                                                            {gameTypeLabel(game.game_type)}
                                                        </span>
                                                    </div>
                                                    <button
                                                        type="button"
                                                        onClick={() => handleDeleteLeft(game)}
                                                        className="shrink-0 rounded-md p-1.5 text-[#ff7474] transition hover:bg-red-50 hover:text-red-500 cursor-pointer"
                                                    >
                                                        <RiDeleteBin6Line className="text-lg" />
                                                    </button>
                                                </div>
                                            );
                                        })}
                                        <div
                                            onDragOver={(e) => {
                                                e.preventDefault();
                                                setDragOverLeftIndex(leftGames.length);
                                            }}
                                            onDrop={(e) => {
                                                e.preventDefault();
                                                handleDropOnLeftPanel(e);
                                            }}
                                            className="h-8"
                                        />
                                    </div>
                                )}
                            </div>

                            <div className="min-h-0 max-h-[60vh] overflow-y-auto rounded-lg p-0.5">
                                {filteredRightGames.length === 0 ? (
                                    <p className="py-8 text-center text-[18px] text-[#bbb]">ไม่พบเกม</p>
                                ) : (
                                    <div className="space-y-2.5">
                                        {filteredRightGames.map((game) => {
                                            const thumbUrl = getThumbnailUrl(game.thumbnail);
                                                const alreadySelected = leftGameIds.has(game.id);
                                                const disabled = alreadySelected;
                                            return (
                                                <div
                                                    key={game.id}
                                                    draggable={!disabled}
                                                    onDragStart={(e) => {
                                                        if (disabled) return;
                                                        handleDragStartRight(e, game);
                                                    }}
                                                    className={`flex min-h-24 items-center gap-2 rounded-lg border border-[#dedede] bg-white px-3 py-2 transition ${disabled ? "cursor-not-allowed opacity-50" : "cursor-grab hover:bg-[#fafafa] active:cursor-grabbing"}`}
                                                >
                                                    <div className="h-13 w-20 shrink-0 overflow-hidden rounded-md border border-[#ececec] bg-[#e0e0e0]">
                                                        {thumbUrl ? (
                                                            <img
                                                                src={thumbUrl}
                                                                alt={game.exercise_name}
                                                                className="h-full w-full object-cover"
                                                            />
                                                        ) : null}
                                                    </div>
                                                    <div className="min-w-0 flex-1 leading-none">
                                                        <p className="truncate text-xs font-bold text-[#2b2b2b]">
                                                            {game.exercise_name}
                                                        </p>
                                                        {game.create_by && (
                                                            <p className="mt-1 truncate text-xs text-[#5f5f5f]">
                                                                โดย {game.create_by}
                                                            </p>
                                                        )}
                                                        <span className="mt-1.5 inline-block rounded-full bg-[#FDEF5C] px-2 py-1 text-xs font-semibold leading-none text-[#2B2B2B]">
                                                            {gameTypeLabel(game.game_type)}
                                                        </span>
                                                    </div>
                                                    <button
                                                        type="button"
                                                        onClick={() => handleSelectGame(game)}
                                                        disabled={disabled}
                                                        className="shrink-0 rounded-md bg-[#1497dc] px-4 py-1.5 text-xs font-medium text-white transition disabled:cursor-not-allowed disabled:opacity-50 hover:bg-[#0f84c3] cursor-pointer"
                                                    >
                                                        {alreadySelected ? "เลือกแล้ว" : "เลือก"}
                                                    </button>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                <div className="flex items-center justify-end gap-4 px-6 py-4">
                    <button
                        type="button"
                        onClick={onClose}
                        className="rounded-md bg-transparent px-3 py-1 text-base font-semibold text-[#1c8bc7] transition hover:underline cursor-pointer"
                    >
                        ยกเลิก
                    </button>
                    <button
                        type="button"
                        onClick={handleSave}
                        className="rounded-md bg-[#1497dc] px-8 py-2 text-base font-semibold text-white transition hover:bg-[#0f84c3] cursor-pointer"
                    >
                        บันทึก
                    </button>
                </div>
            </div>

            {confirmSave && (
                <ConfirmPopUp
                    title="ยืนยันการบันทึก"
                    message={`ต้องการเพิ่มเกมแนะนำใหม่ ${newlyAddedIds.length} เกม ใช่หรือไม่?`}
                    confirmText="ยืนยัน"
                    cancelText="ยกเลิก"
                    isConfirming={isSaving}
                    onConfirm={confirmSaveAction}
                    onCancel={() => setConfirmSave(false)}
                />
            )}

            {confirmDeleteTarget && (
                <ConfirmPopUp
                    title="ยืนยันการลบ"
                    message={`ต้องการลบ "${confirmDeleteTarget.exercise_name}" ออกจากเกมแนะนำ ใช่หรือไม่?`}
                    confirmText="ยืนยัน"
                    cancelText="ยกเลิก"
                    isConfirming={isDeleting}
                    onConfirm={confirmDelete}
                    onCancel={() => setConfirmDeleteTarget(null)}
                />
            )}
        </div>
    );
}
